/**
 * The report's markdown, read into blocks a page can render as text nodes
 * (docs/DECISIONS.md ZD-125 §5).
 *
 * ## What this is, and what it is not
 *
 * `@zevik/report`'s `renderReport` writes one subset of markdown: headings
 * one to three deep, bullets with one level of nesting, blockquotes,
 * paragraphs, and `**strong**` and `_emphasis_` inline. Measured on the
 * committed snapshot (`packages/corpus/review/report-snapshot.md`): 4 `# `,
 * 32 `## `, 147 `### `, 449 `- `, 10 `> `, no backtick, no link, no table,
 * no raw HTML. This reads exactly that subset into a tree whose leaves are
 * strings, and the page renders the strings as React text — never as HTML.
 * A `<script>` in a client's document name is a `<script>` a reader sees,
 * which is what keeps the content route's refusal to render inline (stored
 * XSS against our own console) true of this page as well.
 *
 * It is **not a markdown parser**. A line whose shape this does not know is
 * a paragraph carrying the line verbatim, never dropped and never
 * reinterpreted, so a renderer change upstream degrades to plain text here
 * rather than to a lost sentence.
 *
 * ## This is a format writer, and it carries its own verbatim assertion
 *
 * ZD-057 §4: every writer that turns the report into what a client reads has
 * to show the protected text survives *in that artefact*, because the
 * softening happens downstream of the last place a string exists. A page
 * that parses markdown is such a writer — a caveat could lose a character to
 * an emphasis rule, or a blockquote line could be split in two — so
 * `markdown.test.ts` runs the committed snapshot through this and asserts
 * every line's text comes back contiguous with only the markers removed,
 * and `plainText` exists so that assertion can be made of any block.
 *
 * ## Emphasis follows CommonMark's flanking rule, for a measured reason
 *
 * The snapshot carries underscores inside words in plain text — `pack_qrs-it@0.1.0`
 * in the limits, `resides_in` in an open question, and a whole answer
 * vocabulary of `encryption_in_transit, encryption_at_rest, …` — and a naive
 * `_` toggle splits every one of them. An underscore opens emphasis only at
 * a word's start and closes only at its end; inside a word it is a character.
 * `**` toggles strong wherever it appears, which the subset never
 * contradicts, and inside strong an underscore is never read at all — which
 * is what keeps `- **internet_facing**:` in the disagreements section whole,
 * and is a different guard from the flanking rule. The first version of
 * `markdown.test.ts` used that strong case as the flanking rule's example and
 * its mutation reddened for the wrong reason: the fixture did not vary on the
 * axis the rule is about (ZD-107 §10).
 */

export type Inline =
  { kind: 'text'; text: string } | { kind: 'strong'; text: string } | { kind: 'em'; text: string };

export interface ListItem {
  inlines: Inline[];
  /** One level: the report nests once, under a disagreement's attribute. */
  children: ListItem[];
}

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; inlines: Inline[] }
  | { kind: 'paragraph'; inlines: Inline[] }
  | { kind: 'quote'; inlines: Inline[] }
  | { kind: 'list'; items: ListItem[] };

const isWordChar = (ch: string | undefined): boolean =>
  ch !== undefined && /[\p{L}\p{N}]/u.test(ch);

/** One line's text as inline runs. Every character of `line` is in exactly one run, markers aside. */
export function parseInline(line: string): Inline[] {
  const runs: Inline[] = [];
  let mode: Inline['kind'] = 'text';
  let buffer = '';
  const flush = (): void => {
    if (buffer.length > 0) runs.push({ kind: mode, text: buffer });
    buffer = '';
  };
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (ch === '*' && line[i + 1] === '*' && mode !== 'em') {
      flush();
      mode = mode === 'strong' ? 'text' : 'strong';
      i += 1;
      continue;
    }
    if (ch === '_' && mode !== 'strong') {
      const before = line[i - 1];
      const after = line[i + 1];
      const opens = mode === 'text' && !isWordChar(before) && after !== undefined && after !== ' ';
      const closes = mode === 'em' && !isWordChar(after) && before !== undefined && before !== ' ';
      if (opens || closes) {
        flush();
        mode = mode === 'em' ? 'text' : 'em';
        continue;
      }
    }
    buffer += ch;
  }
  flush();
  return runs;
}

/** The text of a run list with the markers gone — what a reader reads. */
export const plainText = (inlines: readonly Inline[]): string =>
  inlines.map((run) => run.text).join('');

export function parseReport(markdown: string): Block[] {
  const blocks: Block[] = [];
  const lines = markdown.split(/\r?\n/u);
  let paragraph: string[] = [];
  let list: ListItem[] | null = null;

  const endParagraph = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', inlines: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
  };
  const endList = (): void => {
    if (list !== null) {
      blocks.push({ kind: 'list', items: list });
      list = null;
    }
  };

  for (const line of lines) {
    if (line.trim().length === 0) {
      endParagraph();
      endList();
      continue;
    }
    const heading = /^(#{1,3}) (.*)$/u.exec(line);
    if (heading?.[1] !== undefined && heading[2] !== undefined) {
      endParagraph();
      endList();
      blocks.push({
        kind: 'heading',
        level: heading[1].length as 1 | 2 | 3,
        inlines: parseInline(heading[2]),
      });
      continue;
    }
    if (line.startsWith('> ')) {
      endParagraph();
      endList();
      blocks.push({ kind: 'quote', inlines: parseInline(line.slice(2)) });
      continue;
    }
    if (line.startsWith('  - ') && list !== null && list.length > 0) {
      list[list.length - 1]!.children.push({ inlines: parseInline(line.slice(4)), children: [] });
      continue;
    }
    if (line.startsWith('- ')) {
      endParagraph();
      list ??= [];
      list.push({ inlines: parseInline(line.slice(2)), children: [] });
      continue;
    }
    // Anything else is a paragraph line, carried whole.
    endList();
    paragraph.push(line);
  }
  endParagraph();
  endList();
  return blocks;
}
